'use client'

import { useMemo } from 'react'

import useQuickSearchData from '~/components/QuickSearch/hooks/useQuickSearchData'
import { useGetAgreementQuery, useGetProductQuery } from '~/data/api/client/billing'
import { skipWhen } from '~/data/api/hooks/queryOptions'

import {
  type FocusedContextRef,
  buildAgreementContextRef,
  buildChargerContextRef,
  buildProductContextRef,
  buildSiteContextRef,
  buildSynopLinkContextRef,
  buildVehicleContextRef
} from './contextRef'

type EntityCategory = 'charger' | 'site' | 'vehicle' | 'synoplink'

/**
 * First path segment of an entity detail route mapped to its search category.
 * Mirrors the entity detail routes in {@link routeDirectory} (`/chargers/{id}`,
 * `/sites/{id}`, `/vehicles/{id}`, `/links/{id}`).
 */
const ENTITY_SEGMENT_TO_CATEGORY: Readonly<Record<string, EntityCategory>> = {
  chargers: 'charger',
  sites: 'site',
  vehicles: 'vehicle',
  links: 'synoplink'
}

type BillingEntityCategory = 'product' | 'agreement'

/**
 * Billing entities live under `/settings/{segment}/{id}` (and `/edit`). They are not in the QuickSearch
 * data, so their names come from the billing detail queries — normally already in the RTK cache because
 * the detail page fetched them — and fall back to the id until then.
 */
const BILLING_SEGMENT_TO_CATEGORY: Readonly<Record<string, BillingEntityCategory>> = {
  products: 'product',
  agreements: 'agreement'
}

const NON_ENTITY_SEGMENTS = new Set(['new'])

const stripQuery = (path: string): string => path.split('?')[0] ?? path
const normalizePath = (path: string): string => stripQuery(path).replace(/\/+$/, '') || '/'

/**
 * Resolves the "Focus current page" chip into a human-readable {@link FocusedContextRef}.
 *
 * - Entity detail pages (e.g. `/chargers/{id}`) become a typed entity ref labeled
 *   with the entity's name (charger name, site name, etc.), reusing the same cached
 *   QuickSearch data the `@`-mention picker uses. Typing the ref also lets the backend
 *   prefetch entity details, just like a mention.
 * - Every other route resolves to a `page` ref labeled with the page title from the
 *   nav / Cmd+K page list (e.g. "Public Charging").
 * - Anything we can't resolve falls back to the raw pathname.
 */
export default function useFocusPageContext(pathname: string): FocusedContextRef {
  const { chargerItems, siteItems, vehicleItems, synopLinkItems, pages } = useQuickSearchData()

  const billingEntity = useMemo(() => resolveBillingEntity(stripQuery(pathname).split('/').filter(Boolean)), [pathname])
  const productId = billingEntity?.category === 'product' ? billingEntity.id : undefined
  const agreementId = billingEntity?.category === 'agreement' ? billingEntity.id : undefined
  const { data: product } = useGetProductQuery({ productId: productId ?? '' }, skipWhen(!productId))
  const { data: agreement } = useGetAgreementQuery({ id: agreementId ?? '' }, skipWhen(!agreementId))
  const billingName = productId ? product?.name : agreementId ? agreement?.name : undefined

  return useMemo(() => {
    const segments = stripQuery(pathname).split('/').filter(Boolean)

    const entitySegment = segments[0]
    const entityId = segments[1]
    if (segments.length >= 2 && entitySegment && entityId) {
      const category = ENTITY_SEGMENT_TO_CATEGORY[entitySegment]
      if (category) {
        const entityRef = resolveEntityRef(category, entityId, {
          chargerItems,
          siteItems,
          vehicleItems,
          synopLinkItems
        })
        return entityRef ?? { type: category, id: entityId, label: entityId, name: entityId }
      }
    }

    if (billingEntity) {
      return billingEntity.category === 'product'
        ? buildProductContextRef({ id: billingEntity.id, name: billingName })
        : buildAgreementContextRef({ id: billingEntity.id, name: billingName })
    }

    const normalized = normalizePath(pathname)
    const page = pages.find((candidate) => {
      const candidateNormalized = normalizePath(candidate.href)
      if (candidateNormalized !== normalized) return false
      const candidateHasQuery = candidate.href.includes('?')
      const pathnameHasQuery = pathname.includes('?')
      return candidateHasQuery === pathnameHasQuery
    })
    if (page) {
      return { type: 'page', id: pathname, label: page.label }
    }

    return { type: 'page', id: pathname, label: pathname }
  }, [pathname, billingEntity, billingName, chargerItems, siteItems, vehicleItems, synopLinkItems, pages])
}

type BillingEntity = { category: BillingEntityCategory; id: string }

function resolveBillingEntity(segments: string[]): BillingEntity | undefined {
  const [root, segment, entityId] = segments
  if (root !== 'settings' || !segment || !entityId || NON_ENTITY_SEGMENTS.has(entityId)) return undefined
  const category = BILLING_SEGMENT_TO_CATEGORY[segment]
  return category ? { category, id: entityId } : undefined
}

type EntityData = Pick<
  ReturnType<typeof useQuickSearchData>,
  'chargerItems' | 'siteItems' | 'vehicleItems' | 'synopLinkItems'
>

function resolveEntityRef(
  category: EntityCategory,
  id: string,
  data: EntityData
): FocusedContextRef | undefined {
  if (category === 'charger') {
    const charger = data.chargerItems.find((item) => item.chargerId === id)
    return charger ? buildChargerContextRef(charger) : undefined
  }
  if (category === 'site') {
    const site = data.siteItems.find((item) => item.depotId === id)
    return site ? buildSiteContextRef(site) : undefined
  }
  if (category === 'vehicle') {
    const vehicle = data.vehicleItems.find((item) => item.id === id)
    return vehicle ? buildVehicleContextRef(vehicle) : undefined
  }
  const synopLink = data.synopLinkItems.find((item) => item.id === id)
  return synopLink ? buildSynopLinkContextRef(synopLink) : undefined
}
